import { useMemo } from "react";
import { useCatalogo } from "@/modules/biblioteca/hooks/useLivros";
import { useMeusEmprestimos } from "@/modules/biblioteca/hooks/useEmprestimos";

/**
 * Resumo da pessoa na Biblioteca (devolvidos, em andamento, atrasados,
 * último empréstimo e categorias favoritas) — calculado a partir de dados já
 * carregados (meus empréstimos + catálogo), sem query extra ao banco.
 * Usado pela tela "Meu perfil".
 */
export function useResumoBiblioteca(pessoaId: string | undefined) {
  const { data: livros, isLoading: carregandoLivros } = useCatalogo();
  const { data: meusEmprestimos, isLoading: carregandoEmprestimos } =
    useMeusEmprestimos(pessoaId);

  const resumo = useMemo(() => {
    const emprestimos = meusEmprestimos ?? [];
    // Um livro pode ter mais de uma categoria (UX-06): cada empréstimo conta
    // para todas as categorias do livro (ou "Sem categoria" se nenhuma).
    const categoriasPorLivro = new Map(
      (livros ?? []).map((l) => [l.id, l.categorias.map((c) => c.nome)]),
    );

    const devolvidos = emprestimos
      .filter((e) => e.status === "DEVOLVIDO")
      .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
    const emAndamento = emprestimos
      .filter((e) => e.status === "EM_ABERTO")
      .length;
    const atrasados = emprestimos.filter((e) => e.status === "ATRASADO").length;

    // "Último empréstimo" prioriza o livro que a pessoa tem em mãos agora
    // (Em aberto/Atrasado, o mais recente); senão, o Devolvido mais recente.
    // Pendente/Recusado nunca entram — não é posse real.
    const emMaos = emprestimos
      .filter((e) => ["EM_ABERTO", "ATRASADO"].includes(e.status))
      .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
    const ultimoEmprestimo = emMaos[0] ?? devolvidos[0] ?? undefined;

    // Categorias favoritas: só empréstimos que chegaram a acontecer (em
    // andamento, atrasados ou devolvidos) — pendentes e recusados não contam.
    const contagem = new Map<string, number>();
    emprestimos
      .filter((e) => ["EM_ABERTO", "ATRASADO", "DEVOLVIDO"].includes(e.status))
      .forEach((e) => {
        const nomes = categoriasPorLivro.get(e.livro_id);
        (nomes?.length ? nomes : ["Sem categoria"]).forEach((nome) => {
          contagem.set(nome, (contagem.get(nome) ?? 0) + 1);
        });
      });
    const categoriasFavoritas = Array.from(contagem.entries())
      // Empate: "Sem categoria" nunca ganha de uma categoria de verdade.
      .sort((a, b) => b[1] - a[1] || Number(a[0] === "Sem categoria") - Number(b[0] === "Sem categoria"))
      .slice(0, 3);

    return {
      devolvidos: devolvidos.length,
      emAndamento,
      atrasados,
      totalPedidos: emprestimos.length,
      ultimoEmprestimo,
      categoriasFavoritas,
    };
  }, [meusEmprestimos, livros]);

  return { ...resumo, carregando: carregandoLivros || carregandoEmprestimos };
}
